import { useEffect, useState } from "react";
import api from "@/lib/api";

function colorFor(pct) {
  if (pct === 0) return "bg-neutral-100 text-neutral-400";
  if (pct < 50) return "bg-blue-50 text-blue-700";
  if (pct < 80) return "bg-emerald-50 text-emerald-700";
  if (pct <= 110) return "bg-emerald-200 text-emerald-900";
  if (pct <= 130) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-700";
}

function formatWeek(iso) {
  const d = new Date(iso);
  return `${d.toLocaleString("en", { month: "short" })} ${d.getDate()}`;
}

export default function UtilizationHeatmap({ refreshKey = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/utilization?weeks=4").then(r => setData(r.data)).catch(() => {});
  }, [refreshKey]);

  if (!data) return null;

  return (
    <div className="border border-neutral-200 bg-white" data-testid="utilization-heatmap">
      <div className="px-5 py-4 border-b border-neutral-200">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">Capacity</div>
        <div className="font-display text-lg font-bold">Utilization · last 4 weeks</div>
        <div className="text-xs text-neutral-500 mt-1">Each row uses that member's individual weekly capacity (set in the table below).</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left px-4 py-2 font-semibold">Member</th>
              <th className="text-center px-3 py-2 font-semibold">Target</th>
              {data.week_starts.map((ws) => (
                <th key={ws} className="text-center px-3 py-2 font-mono font-semibold">{formatWeek(ws)}</th>
              ))}
              <th className="text-right px-4 py-2 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(row => {
              const total = row.weeks.reduce((s, w) => s + w.hours, 0);
              return (
                <tr key={row.user_id} className="border-t border-neutral-100" data-testid={`util-row-${row.user_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{row.role.replace("_", " ")}</div>
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs text-neutral-500">{row.weekly_capacity_hours}h</td>
                  {row.weeks.map((w, i) => (
                    <td key={w.week_start} className="px-2 py-2 text-center" data-testid={`util-cell-${row.user_id}-${i}`}>
                      <div className={`inline-flex flex-col items-center justify-center w-16 py-2 rounded-sm font-mono ${colorFor(w.pct)}`}>
                        <div className="text-sm font-semibold">{w.hours}h</div>
                        <div className="text-[9px] uppercase tracking-widest opacity-80">{w.pct}%</div>
                      </div>
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right font-mono font-semibold">{total.toFixed(1)}h</td>
                </tr>
              );
            })}
            {data.rows.length === 0 && (
              <tr><td colSpan={data.week_starts.length + 3} className="px-4 py-8 text-center text-xs text-neutral-500">No team members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500 px-5 py-3 border-t border-neutral-200 font-semibold">
        <span>Legend:</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-neutral-100 rounded-sm border border-neutral-200"></span>idle</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-blue-50 rounded-sm border border-blue-200"></span>&lt;50%</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-200 rounded-sm border border-emerald-300"></span>at capacity</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-100 rounded-sm border border-amber-200"></span>over</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-red-100 rounded-sm border border-red-200"></span>burnout risk</span>
      </div>
    </div>
  );
}
